import type { QueryClient } from "@tanstack/react-query";
import { ssGet, ssRemove, ssSet } from "./auth";
import { getRunningBuildId } from "./appVersion";
import { store } from "../store/store";

/**
 * Зареждането на код, който се тегли чак при отваряне на модул
 * (`routes/lazyPage.tsx`, docs/architecture.md §Код при отваряне). Човекът не
 * бива да вижда нищо от механизма — нито бутон, нито съобщение.
 *
 * Провал тук на практика значи, че прозорецът изпълнява СТАР билд, а неговият
 * файл вече го няма: service worker-ът пази и предишния билд (`src/sw.ts`), така
 * че това остава само за прозорец, изостанал с два и повече деплоя, или без
 * service worker (или мрежата е паднала по време на тегленето). Затова: тихо
 * презареждане на новия билд (САМО ако човекът още е на модула, който се
 * отваряше, и това не прекъсва запис) → иначе грешка към границата.
 *
 * Повторен опит в същия прозорец НЯМА смисъл: браузърът помни провален
 * `import()` до края на документа и при втори опит връща същата грешка, без
 * да тегли наново. Затова провалът се помни (`lazyPage.tsx` не прави ново
 * lazy), а единственият път обратно е презареждането.
 */

// Маркер „прозорецът е отворен от тихото презареждане". Консумира се при
// старт: ако модулът пада и в новия прозорец, презареждането не е помогнало —
// първият провал отива към границата вместо в примка. Следващ провал (човекът
// е отворил модул наново) пак може да презареди.
const RELOADED_KEY = "app:chunkReload";
let mayReload = ssGet(RELOADED_KEY) === null;
ssRemove(RELOADED_KEY);

// Записите през React Query в ход (мутациите — поръчка, сторно, фактура…):
// тихото презареждане ги изчаква. Подава се от `main.tsx` при старт.
let queryClient: QueryClient | null = null;

export function initChunkLoadRecovery(client: QueryClient): void {
  queryClient = client;
}

/** Провалено зареждане на модул — различава се от грешка в самата страница. */
export class ChunkLoadError extends Error {
  /** При следващия показ от границата — нов опит за презареждане. */
  reloadWhenShown: boolean;

  constructor(cause: unknown, reloadWhenShown: boolean) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = "ChunkLoadError";
    this.cause = cause;
    this.reloadWhenShown = reloadWhenShown;
  }
}

type ReloadDecision = "reloading" | "refused" | "left";

export async function importWithRecovery<T>(load: () => Promise<T>): Promise<T> {
  // Адресът на модула, който се отваря — запомнен СЕГА, не при провала: ако
  // човекът междувременно е отишъл другаде, провалът не бива да презареди
  // екрана, на който вече работи
  const openedPath = window.location.pathname;
  try {
    const loaded = await load();
    mayReload = true;
    return loaded;
  } catch (error) {
    const decision = await reloadIntoServerBuild(openedPath);
    if (decision === "reloading") return new Promise<T>(() => {});
    // Човекът е отишъл другаде, докато чакахме — следващото отваряне на
    // модула е новият опит.
    throw new ChunkLoadError(error, decision === "left");
  }
}

/**
 * Границата показва грешката. Първият показ идва направо от решението в
 * `importWithRecovery`; всеки следващ значи, че човекът е отворил модула
 * наново (навигация обратно, друг `:page`) — тогава пак пробваме тихото
 * презареждане. Отделен React рендер на същия провал не минава оттук.
 */
export function chunkLoadErrorShown(error: ChunkLoadError): void {
  if (!error.reloadWhenShown) {
    error.reloadWhenShown = true;
    return;
  }
  // Границата се показва на адреса на модула — човекът е там сега
  void reloadIntoServerBuild(window.location.pathname);
}

/**
 * Тихо презареждане, когато файлът на стария билд липсва. Отказва: в dev
 * (няма хеширан билд), в локален режим срещу касата, при първия провал в
 * прозорец, отворен от самото презареждане. Само ако човекът още е на адреса
 * на модула (`modulePath`) — иначе „left". Закриване на сметка в ход
 * (`bills.closingBills` — чакането на фискалния принтер живее в паметта) и
 * записите през React Query (мутациите — поръчка, сторно, фактура…) се
 * изчакват, вместо да се прекъснат; след чакането адресът и режимът се
 * проверяват наново. Черновите на склада (`useDraftSync`, преки заявки) не се
 * броят: своята е в localStorage до 400 ms, а напускането на листа я записва.
 */
async function reloadIntoServerBuild(modulePath: string): Promise<ReloadDecision> {
  if (!getRunningBuildId()) return "refused";
  if (!isCloudMode()) return "refused";
  if (!mayReload) {
    mayReload = true;
    return "refused";
  }
  if (window.location.pathname !== modulePath) return "left";
  await waitForNoWorkInProgress();
  if (window.location.pathname !== modulePath) return "left";
  // Докато чакахме (напр. фискалния принтер), терминалът може да е минал на касата
  if (!isCloudMode()) return "refused";
  ssSet(RELOADED_KEY, "1");
  window.location.reload();
  return "reloading";
}

const isCloudMode = () => store.getState().edge.mode === "cloud";

function waitForNoWorkInProgress(): Promise<void> {
  const isIdle = () =>
    store.getState().bills.closingBills.length === 0 && (queryClient?.isMutating() ?? 0) === 0;
  if (isIdle()) return Promise.resolve();
  return new Promise((resolve) => {
    const unsubscribers: (() => void)[] = [];
    const resolveWhenIdle = () => {
      if (!isIdle()) return;
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      resolve();
    };
    unsubscribers.push(store.subscribe(resolveWhenIdle));
    if (queryClient) unsubscribers.push(queryClient.getMutationCache().subscribe(resolveWhenIdle));
  });
}
